/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { ArrowUpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useForceUpdateGate } from "@/hooks/useForceUpdateGate";
import { APP_STORE_UPDATE_URL } from "@/utils/forceUpdateGate";

/**
 * App-wide launch-time build-version gate. Renders nothing unless the gate
 * decides the installed build is below the supported threshold.
 *
 * - hard-block (forceUpdate true): non-dismissible — no close button, no
 *   backdrop/escape dismiss, no "Later". Single "Update Now" CTA.
 * - soft-nudge (forceUpdate false): dismissible — "Later" + "Update".
 *
 * Built on Radix primitives directly (not the shared DialogContent, which
 * always renders a close button) so the hard-block mode can fully suppress
 * every dismissal affordance.
 */
export default function ForceUpdateModal() {
  const { t } = useTranslation();
  const { show, dismissible, message } = useForceUpdateGate();
  const [dismissed, setDismissed] = useState(false);

  const open = show && !(dismissible && dismissed);

  const openAppStore = () => {
    // `_system` opens the App Store app on Capacitor iOS (same external-link
    // mechanism the rest of the app uses).
    window.open(APP_STORE_UPDATE_URL, "_system");
  };

  // Suppress every auto-dismiss path for the hard block. For the soft nudge,
  // outside/escape close it via the same path as the Later button.
  const blockDismiss = (e: Event) => {
    if (!dismissible) e.preventDefault();
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        // Only the soft nudge may close; the hard block ignores close intent.
        if (!next && dismissible) setDismissed(true);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/80 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onPointerDownOutside={blockDismiss}
          onInteractOutside={blockDismiss}
          onEscapeKeyDown={blockDismiss}
          className={cn(
            "fixed left-[50%] top-[50%] z-[100] grid w-full max-w-[360px] translate-x-[-50%] translate-y-[-50%]",
            "gap-4 border bg-background p-6 shadow-lg sm:rounded-lg",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          )}
        >
          <div className="flex flex-col items-center text-center gap-3">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="text-primary"
            >
              <ArrowUpCircle size={40} strokeWidth={1.75} />
            </motion.div>
            <DialogPrimitive.Title className="text-lg font-semibold">
              {dismissible ? t("forceUpdate.softTitle") : t("forceUpdate.title")}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground leading-relaxed">
              {message ?? (dismissible ? t("forceUpdate.softBody") : t("forceUpdate.body"))}
            </DialogPrimitive.Description>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={openAppStore}
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold"
            >
              {dismissible ? t("forceUpdate.update") : t("forceUpdate.updateNow")}
            </motion.button>
            {dismissible && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setDismissed(true)}
                className="w-full py-2.5 rounded-xl bg-muted text-muted-foreground text-sm font-medium"
              >
                {t("forceUpdate.later")}
              </motion.button>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
